/**
 * Audit service for MetricData Dev Tool
 * Handles audit queries, record count validation, and UUID detection
 *
 * @module MetricDataDevTool/services/auditService
 */

import { API, graphqlOperation } from 'aws-amplify';
import { metricDataByClientDate } from 'graphql/queries';
import { QUERY_CONFIG } from '../constants';
import { isUuidFormat, validateRecordCount as validateCount } from '../utils/auditValidators';

/**
 * Group records by metricName
 *
 * @param {Array} records - Array of metric records
 * @returns {Object} Records grouped by metricName
 */
export function groupByMetricName(records) {
  return records.reduce((acc, record) => {
    if (!acc[record.metricName]) {
      acc[record.metricName] = [];
    }
    acc[record.metricName].push(record);
    return acc;
  }, {});
}

/**
 * Group records by metricName and date
 *
 * @param {Array} records - Array of metric records
 * @returns {Object} Records grouped by metricName#date key
 */
export function groupByMetricNameAndDate(records) {
  return records.reduce((acc, record) => {
    const key = `${record.metricName}#${record.date}`;
    if (!acc[key]) {
      acc[key] = {
        metricName: record.metricName,
        date: record.date,
        records: []
      };
    }
    acc[key].records.push(record);
    return acc;
  }, {});
}

/**
 * Validate record count against expected count
 * Re-export from auditValidators for backward compatibility
 *
 * @param {string} metricName - The metric name
 * @param {number} actualCount - Actual record count
 * @returns {Object} Validation result with expected, actual, isValid, variance
 */
export function validateRecordCount(metricName, actualCount) {
  return validateCount(metricName, actualCount);
}

/**
 * Analyze metricIds to identify UUID vs composite format
 *
 * @param {Array} records - Array of metric records
 * @returns {Object} Analysis with UUID and composite counts and details
 */
export function analyzeMetricIds(records) {
  const uuidRecords = records.filter(r => isUuidFormat(r.metricId));
  const compositeRecords = records.filter(r => !isUuidFormat(r.metricId));

  return {
    totalRecords: records.length,
    uuidCount: uuidRecords.length,
    compositeCount: compositeRecords.length,
    uuidRecords: uuidRecords.map(r => ({
      metricId: r.metricId,
      metricName: r.metricName,
      storeId: r.storeId,
      date: r.date,
      dayPart: r.dayPart,
      diningOption: r.diningOption,
      createdAt: r.createdAt
    }))
  };
}

/**
 * Run audit for multiple stores and a date range
 * Fetches all records and analyzes them for count discrepancies and UUID format IDs
 * Optimized to query by date (not by store) to leverage byClientDate index efficiently
 *
 * @param {Object} params - Audit parameters
 * @param {string} params.clientId - Client ID
 * @param {Array<string>} params.storeIds - Array of store IDs to audit
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {number} [params.retryCount=0] - Current retry attempt
 * @returns {Promise<Object>} Object with results keyed by storeId
 * @throws {Error} If query fails
 */
export async function runAudit({ clientId, storeIds, startDate, endDate, retryCount = 0 }) {
  const MAX_RETRIES = 3;

  console.log('🔍 [Audit Service] Starting audit:', {
    clientId,
    storeCount: storeIds.length,
    storeIds,
    startDate,
    endDate,
    retryCount
  });

  try {
    // Fetch all records for ALL stores in date range using byClientDate index
    // This is more efficient than filtering per-store
    let allRecords = [];
    let nextToken = null;
    let pageCount = 0;

    do {
      pageCount++;
      console.log(`📄 [Audit Service] Fetching page ${pageCount}...`);

      const queryParams = {
        clientId,
        date: {
          between: [startDate, endDate]
        },
        limit: QUERY_CONFIG.PAGE_SIZE,
        nextToken,
        sortDirection: 'ASC'
      };

      console.log('🔍 [Audit Service] Query params:', JSON.stringify(queryParams, null, 2));

      const response = await API.graphql(
        graphqlOperation(metricDataByClientDate, queryParams)
      );

      const items = response.data.metricDataByClientDate.items || [];

      // Filter to only requested stores client-side
      const filteredItems = items.filter(item => storeIds.includes(item.storeId));
      allRecords = allRecords.concat(filteredItems);
      nextToken = response.data.metricDataByClientDate.nextToken;

      console.log(`✅ [Audit Service] Page ${pageCount}: ${items.length} total, ${filteredItems.length} for selected stores (Total: ${allRecords.length})`);
    } while (nextToken);

    console.log(`📊 [Audit Service] Total records fetched: ${allRecords.length}`);

    // Group records by storeId first, then by metric/date
    const recordsByStore = allRecords.reduce((acc, record) => {
      if (!acc[record.storeId]) {
        acc[record.storeId] = [];
      }
      acc[record.storeId].push(record);
      return acc;
    }, {});

    // Analyze each store's records
    const resultsByStore = {};

    for (const storeId of storeIds) {
      const storeRecords = recordsByStore[storeId] || [];

      // Group by metric name AND date
      const groupedByMetricAndDate = groupByMetricNameAndDate(storeRecords);

      // Analyze each metric/date combination
      const auditResults = Object.values(groupedByMetricAndDate).map((group) => {
        const { metricName, date, records } = group;
        const countValidation = validateRecordCount(metricName, records.length);
        const idAnalysis = analyzeMetricIds(records);

        return {
          metricName,
          date,
          ...countValidation,
          ...idAnalysis,
          records // Full record list for drill-down
        };
      });

      resultsByStore[storeId] = auditResults;
    }

    console.log('✅ [Audit Service] Audit complete:', {
      storesAnalyzed: Object.keys(resultsByStore).length,
      totalRecords: allRecords.length
    });

    return resultsByStore;
  } catch (error) {
    // Check if it's a throttling error
    const isThrottlingError =
      error.message?.includes('Throughput exceeds') ||
      error.message?.includes('ThrottlingException') ||
      error.errors?.[0]?.message?.includes('Throughput exceeds') ||
      error.errors?.[0]?.message?.includes('ThrottlingException');

    if (isThrottlingError && retryCount < MAX_RETRIES) {
      const delayMs = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
      console.warn(`⚠️ [Audit Service] Throttled, retrying in ${delayMs}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);

      await new Promise(resolve => setTimeout(resolve, delayMs));

      return runAudit({ clientId, storeIds, startDate, endDate, retryCount: retryCount + 1 });
    }

    // If not throttling or max retries reached, throw the error
    console.error('❌ [Audit Service] Error:', error);
    throw error;
  }
}
